<template>
  <div class="cms-editor d-flex flex-column">
    <div class="cms-editor-ctn d-flex flex-row flex-grow-1">
      <div class="toolbox-ctn font-weight-light flex-shrink-0">
        <div class="toolbox my-6">
          <div class="tool-section-header">{{$t('cms_sku_specific_elements')}}</div>
          <draggable  :clone="cloneTool" :list="skuTools" :sort="false" :group="{ name: 'a', pull: checkClone, put: false }">
            <div v-show="isAvailable(`editor:tabs:tools:${tool.type}`)" :data-type="tool.type" @click="addTool(tool)" :class="{ 'hide': tool.scantrustOnly && !isAdmin, 'disabled': getToolCount(tool.type) >= getLimit(`editor:tabs:tools:${tool.type}`)}" class="tool pa-2 my-2" v-for="(tool, index) in skuTools" :key="`sku-tool-${index}`">
              <v-row class="ma-0">
                <v-icon class="mr-2" color="black">{{tool.icon}}</v-icon>
                {{tool.description}}
                <v-spacer></v-spacer>
                <v-icon color="black">mdi-auto-fix</v-icon>
              </v-row>
            </div>
          </draggable>
          <v-divider class="my-5"></v-divider>
          <div class="tool-section-header">{{$t('cms_standard_webpage_elements')}}</div>
          <draggable  :clone="cloneTool" :list="tools" :sort="false" :group="{ name: 'a', pull: checkClone, put: false }">
            <button
              v-show="isAvailable(`editor:tabs:tools:${tool.type}`)"
              :data-type="tool.type"
              @click="addTool(tool)"
              :class="{ 'hide': tool.scantrustOnly && !isAdmin, 'disabled': tool.type === 'photo_auth'? getToolCount(tool.type) == 1 : getToolCount(tool.type) >= getLimit(`editor:tabs:tools:${tool.type}`)}"
              class="tool pa-2 my-1"
              v-for="(tool, index) in tools"
              :key="`tool-${index}`"
              :disabled="tool.type === 'photo_auth'? getToolCount(tool.type) == 1 : getToolCount(tool.type) >= getLimit(`editor:tabs:tools:${tool.type}`)"
            >
              <v-row class="ma-0">
                <v-col cols="auto" class="pa-0 mr-auto">
                  <v-icon color="black">{{tool.icon}}</v-icon>
                  {{tool.description}}
                </v-col>
                <v-col v-if="hasLimit(`editor:tabs:tools:${tool.type}`)" cols="auto" class="pa-0">
                  <span class="limit">{{$t('cms_up_to')}} {{getLimit(`editor:tabs:tools:${tool.type}`)}}</span>
                </v-col>
              </v-row>
            </button>
          </draggable>
          <template v-if="isAvailable('editor:tabs:tools:elabel')">
            <v-divider class="my-5"></v-divider>
            <div class="tool-section-header">
              {{$t('cms_elabel_elements')}}
              <info-tooltip :content="$t('cms_elabel_elements_hint')"></info-tooltip>
            </div>
            <draggable :clone="cloneTool" :list="elabelTools" :sort="false" :group="{ name: 'a', pull: checkClone, put: false }">
              <button
                :data-type="tool.type"
                @click="addTool(tool)"
                class="tool pa-2 my-1 d-flex align-center text-left"
                v-for="(tool, index) in elabelTools"
                :key="`elabel-tool-${index}`"
              >
                <v-icon color="black" class="mr-2">{{tool.icon}}</v-icon>
                <span>{{tool.description}}</span>
              </button>
            </draggable>
          </template>
        </div>

        <div v-if="!isAvailable('editor:authentication') && !isSelfServe" align-content="center" justify="start">
          <span class="font-weight-bold">{{$t('cms_need_more_elements')}}</span>
          <info-tooltip class="more-tooltip ml-3" :content="$t('cms_upgrade_unlock_features')"></info-tooltip>
        </div>

      </div>
      <div class="content-ctn px-2 flex-grow-1 flex-shrink-1">
        <v-text-field :disabled="!isAvailable('editor:global:title')" class="page-title" dense v-model="pageTitle" :placeholder="$t('cms_enter_page_title')"></v-text-field>
        <div class="drop-area-ctn flex-grow-1 flex-shrink-1 pa-2">
          <div class="drop-area-header  font-weight-bold">{{$t('cms_content_layout_drag')}}</div>
          <draggable :disabled="editingContent" class="drop-area flex-shrink-1" v-model="tabBody" group="a">
            <div class="tool my-2" v-for="tool in tabBody" :key="`active-tool-${tabId}-${tool.key}`">
              <cms-tool :icon="`${tool.icon}`" :type="tool.type" :tool-key="tool.key" :data="tool.data" :styles="tool.styles" :tab-id="tabId" :lang="defaultLanguage">
              </cms-tool>
            </div>
          </draggable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import draggable from 'vuedraggable'
import CMSTool from '@/components/editor/CMSTool'
import utils from '@/utils/generateRandomKey'
import { mapGetters, mapMutations } from 'vuex'
import InfoTooltip from '@/components/utils/InfoTooltip'
import amplitude from 'amplitude-js'

export default {
  name: 'CMS',
  props: ['enabled', 'tabId'],
  components: {
    draggable,
    'cms-tool': CMSTool,
    InfoTooltip
  },
  computed: {
    ...mapGetters(['stcTabs', 'getTabVisibility', 'defaultLanguage', 'isAdmin', 'editingContent', 'isAvailable', 'hasLimit', 'getLimit', 'getToolCount', 'isSelfServe']),
    tabName: {
      get () {
        return this.$store.getters.getTabName(this.tabId)
      },
      set (name) {
        this.$store.dispatch('updateTabName', { tabId: this.tabId, name })
      }
    },
    pageTitle: {
      set (value) {
        this.setPageTitle(value)
      },
      get () {
        return this.$store.getters.pageTitle
      }
    },
    tabIcon: {
      get () {
        return this.$store.getters.getTabData(this.tabId).icon
      },
      set (icon) {
        this.$store.dispatch('updateTabIcon', { tabId: this.tabId, icon })
      }
    },
    tabBody: {
      get () {
        return this.$store.getters.getTabData(this.tabId).body
      },
      set (body) {
        this.$store.dispatch('updateTabBody', { tabId: this.tabId, body })
      }
    }
  },
  watch: {
    stcTabs: {
      deep: true,
      handler (tabs) {
        this.tabVisibility = this.getTabVisibility(this.tabId)
      }
    },
    tabVisibility: {
      deep: true,
      handler (visibility) {
        this.$store.dispatch('updateTabVisibility', { tabId: this.tabId, visibility })
      }
    }
  },
  mounted () {
    this.tabVisibility = this.getTabVisibility(this.tabId)
    if (!this.tabName) {
      this.tabName = this.$store.getters.getDefaultTabName(this.tabId)
    }
  },
  methods: {
    ...mapMutations({
      setPageTitle: 'SET_PAGE_TITLE'
    }),

    checkClone (to, from, elem) {
      if (elem.dataset.type) {
        if (this.hasLimit(`editor:tabs:tools:${elem.dataset.type}`)) {
          if (this.getToolCount(elem.dataset.type) >= this.getLimit(`editor:tabs:tools:${elem.dataset.type}`)) {
            return false
          } else {
            return 'clone'
          }
        } else {
          return 'clone'
        }
      }
    },

    addTool (tool) {
      if (this.hasLimit(`editor:tabs:tools:${tool.type}`)) {
        if (this.getToolCount(tool.type) >= this.getLimit(`editor:tabs:tools:${tool.type}`)) {
          // Could show message here
          return
        }
      }
      const clone = this.cloneTool(tool)
      clone.key = utils.generateRandomKey()
      this.tabBody.push(clone)
    },

    selectIcon (icon) {
      this.$store.dispatch('updateTabIcon', { tabId: this.tabId, icon })
    },

    cloneTool (element) {
      amplitude.getInstance().logEvent('Add Widget', { type: element.type })
      const newObj = Object.assign({ key: utils.generateRandomKey() }, element)
      newObj.data = JSON.parse(JSON.stringify(this.defaultToolData[element.type]))

      if (newObj.data.content) {
        newObj.data.content[this.defaultLanguage] = ''
      }

      if (newObj.data.url) {
        newObj.data.url[this.defaultLanguage] = 'https://'
      }

      if (newObj.data.height) {
        newObj.data.height[this.defaultLanguage] = 500
      }

      if (newObj.data.src) {
        newObj.data.src[this.defaultLanguage] = ''
      }

      return newObj
    },

    openIconPicker () {
      this.iconPickerModal = true
    },

    deleteTab () {
      this.$emit('delete-tab', this.tabId)
    }
  },
  data () {
    return {
      tabVisibility: {},
      iconPickerModal: false,
      list: [],
      defaultToolData: {
        large_text: {
          content: {}
        },
        text: {
          content: {}
        },
        button: {
          content: {},
          url: {}
        },
        file: {
          content: {},
          url: {}
        },
        typeform: {
          content: {},
          url: {}
        },
        divider: {
          content: {}
        },
        image: {
          src: {}
        },
        social: {
          content: {},
          url: {},
          icon: ''
        },
        video: {
          content: {}
        },
        iframe: {
          url: {},
          height: {}
        },
        consume: {
          mode: 'auto',
          content: {},
          consumed: {},
          button: {}
        },
        gallery: {
          src: {}
        },
        // Product tools
        product_name: {
          content: {}
        },
        product_description: {
          content: {}
        },
        product_image: {
          content: {}
        },
        scan_result: {
          content: {}
        },
        photo_auth: {
          button: {}
        },
        elabel: {
          content: {}
        },
        elabel_business_operator: {},
        elabel_geographical_indications: {},
        elabel_ingredients: {},
        elabel_nutrition: {},
        elabel_responsible_consumption: {},
        elabel_sustainability: {}
      },
      skuTools: [
        {
          icon: '$image',
          description: 'Product Picture',
          type: 'product_image'
        },
        {
          icon: '$text',
          description: 'Product Name',
          type: 'product_name'
        },
        {
          icon: '$text',
          description: 'Product Description',
          type: 'product_description'
        }
      ],
      tools: [
        {
          icon: 'mdi-format-title',
          description: 'Big Text',
          type: 'large_text'
        },
        {
          icon: '$text',
          description: 'Text Block',
          type: 'text'
        },
        {
          icon: '$button',
          description: 'Call-To-Action Button',
          type: 'button'
        },
        {
          icon: 'mdi-file-pdf-box',
          description: 'File Download',
          type: 'file'
        },
        {
          icon: '$image',
          description: 'Picture',
          type: 'image'
        },
        {
          icon: '$typeform',
          description: 'Typeform Pop-up',
          type: 'typeform'
        },
        {
          icon: '$social',
          description: 'Social Media Links',
          type: 'social'
        },
        {
          icon: '$divider',
          description: 'Divider',
          type: 'divider'
        },
        {
          icon: '$video',
          description: 'Video',
          type: 'video'
        },
        {
          icon: 'mdi-iframe-outline',
          description: 'IFrame',
          type: 'iframe'
        },
        {
          icon: '$consume',
          description: 'Consume Code',
          type: 'consume',
          scantrustOnly: true
        },
        {
          icon: 'mdi-security',
          description: 'Authentication Result',
          type: 'scan_result'
        },
        {
          icon: 'mdi-camera-wireless',
          description: 'Authentication app',
          type: 'photo_auth'
        },
        {
          icon: '$nutrition',
          description: 'e-Label Button',
          type: 'elabel'
        }

        // {
        //   icon: '$image',
        //   description: 'ePac Gallery',
        //   type: 'gallery',
        //   scantrustOnly: true
        // }
      ],
      elabelTools: [
        {
          icon: '$nutrition',
          description: 'e-Label Business Operator',
          type: 'elabel_business_operator'
        },
        {
          icon: '$nutrition',
          description: 'e-Label Geographical Indication',
          type: 'elabel_geographical_indications'
        },
        {
          icon: '$nutrition',
          description: 'e-Label Ingredients',
          type: 'elabel_ingredients'
        },
        {
          icon: '$nutrition',
          description: 'e-Label Nutrition',
          type: 'elabel_nutrition'
        },
        {
          icon: '$nutrition',
          description: 'e-Label Responsible Consumption',
          type: 'elabel_responsible_consumption'
        },
        {
          icon: '$nutrition',
          description: 'e-Label Sustainability',
          type: 'elabel_sustainability'
        }
      ]
    }
  }
}

</script>

<style lang="sass">

@import '@/variables.sass'

.page-title
  input
    text-align: center

.hide
  display: none

.cms-editor
  height: 100%
  overflow-y: auto
  padding: 10px
  background: white

.tab-name
  border: 1px solid #6666661c
  padding-right: 2px !important

.tab-settings-ctn
  background: white
  position: relative
  .delete-btn
    position: absolute
    top: 5px
    right: 5px

.enable-ctn
  padding-right: 40px
  width: 268px
  .v-input__icon
    svg
      fill: rgba(var(--primary))

.cms-editor-ctn
  background: white
  .toolbox-ctn
    max-width: 170px
    @include md
      max-width: 270px
    .more-tooltip
      padding-top: 5px
  .content-ctn
    // max-width: 500px
.tool-section-header
  color: black
  font-weight: bold
  font-size: 12px

.tool
  width: 100%
  background: $bg-grey
  border-radius: 6px
  cursor: grab
  box-shadow: 0 2px 0 0 rgba(0, 0, 0, 0.3)
  &.disabled
    opacity: 0.5
    cursor: not-allowed
  .limit
    font-size: 12px

.drop-area-ctn
  min-width: 265px
  border: 2px dashed rgba($pastel, 0.5)
  .drop-area-header
    width: 100%
    text-align: center
    color: rgba($pastel, 0.5)
  .drop-area
    height: 100%

</style>
